import { FC, useEffect, useState, useRef } from "react"
import { NewsService, News } from "../sdk"
import Card from "../components/Card"
import Button from "../components/Button"
import PageLoader from "../components/PageLoader"
import useError from "../hooks/useError"
import { formatDateHour } from "../helpers/date"
import Modal from "../components/Modal"
import EditNewsForm from "../components/EditNewsForm"
import { toast } from "react-toastify"
import AddNewsForm from "../components/AddNewsForm"
import Row from "../components/Row"
import useDebounce from "../hooks/useDebounce"
import Input from "../components/Input"
import Icon from "../components/Icon"
import Col from "../components/Col"
import useConfirmModal from "../hooks/useConfirmModal"
import classNames from "classnames"
import Message from "../components/Message"


const NewsPage: FC = () => {
  const [loading, setLoading] = useState<boolean>(true)
  const [newsUpdating, setNewsUpdating] = useState<boolean>(false)

  const news = useRef<News[] | null>(null)
  const [currentNews, setCurrentNews] = useState<News | null>(null)
  const [visibleNews, setVisibleNews] = useState<News[] | null>(null)

  const [addNewsModalOpen, setAddNewsModalOpen] = useState<boolean>(false)

  const { handleError } = useError()

  const [searchBarValue, setSearchBarValue] = useState<string>("")
  const debouncedValue = useDebounce(searchBarValue, 500)

  const { openConfirmModal } = useConfirmModal()

  const filterResults = (): void => {
    if (news.current) {
      setVisibleNews(news.current!.filter(({ title, description }) => title.toLowerCase().includes(debouncedValue.toLowerCase()) || description.toLowerCase().includes(debouncedValue.toLowerCase())))
    }
  }

  const renderNews = async(): Promise<void> => {
    try {
      const { data: newsData } = await NewsService.listNews({
        paginator: {
          limit: 100,
          offset: 0
        }
      })
      news.current = newsData
      setVisibleNews(newsData)
      setLoading(false)
    } catch (err) {
      handleError(err)
    }
  }

  const deleteNews = async(newsId: number): Promise<void> => {
    setNewsUpdating(true)
    try {
      await NewsService.deleteNews({ handler: { newsId } })
      toast.success("News eliminata correttamente")
      void renderNews()
    } catch (err) {
      handleError(err)
    }
    setNewsUpdating(false)
  }

  useEffect(() => {
    void renderNews()
  }, [])

  useEffect(() => {
    filterResults()
  }, [debouncedValue])

  return (
    <>
      {loading ?
        <PageLoader className="h-screen w-screen top-0 left-0 fixed"/>
        :
        <>
          <div className="flex items-center gap-4 mb-8">
            <h1 className="text-3xl font-bold">News</h1>

            {news.current && news.current.length > 0 &&
              <div className="w-96">
                <Input
                  placeholder="Cerca..."
                  onChange={setSearchBarValue}
                  value={searchBarValue}
                  suffix={
                    searchBarValue !== "" &&
                    <span
                      onClick={(): void => {
                        setSearchBarValue("")
                      }}
                      className="hover:cursor-pointer hover:text-gray-500 w-8 h-8 flex justify-center items-center"
                    >
                      <Icon
                        name="x-close"
                        size={20}
                      />
                    </span>
                  }
                />
              </div>
            }

            <Button
              onClick={(): void => setAddNewsModalOpen(true)}
            >
              Aggiungi
            </Button>
          </div>
          {visibleNews?.length
            ?
            <div className={classNames("mt-8 grid grid-cols-3 gap-4", newsUpdating && "opacity-50 pointer-events-none")}>
              {visibleNews.map((news) => {
                const {
                  id,
                  startPublicTimestamp,
                  endPublicTimestamp,
                  title,
                  description
                } = news
                return (
                  <Card heading={title} key={id}>

                    <div className="flex-1 italic pb-4 mb-2 border-b">{description}</div>

                    <ul>
                      <Row as="li" className="card__row" justify="between">
                        <div className="card__row-title">Data di pubblicazione</div>
                        <div>{startPublicTimestamp ? formatDateHour(startPublicTimestamp) : "Non definita"}</div>
                      </Row>

                      <Row as="li" className="card__row" justify="between">
                        <div className="card__row-title">Data di scadenza</div>
                        <div>{endPublicTimestamp ? formatDateHour(endPublicTimestamp) : "Non definita"}</div>
                      </Row>
                    </ul>

                    <div className="grid grid-cols-12 gap-x-4">
                      <Col lg={6}>
                        <Button
                          className="mt-4 w-full"
                          onClick={(): void => {
                            setCurrentNews(news)
                          }}
                        >
                          Modifica
                        </Button>
                      </Col>
                      <Col lg={6}>
                        <Button
                          className="mt-4 w-full"
                          theme="danger"
                          onClick={(): void => {
                            openConfirmModal({
                              content: <div className="my-8">Eliminare News <b>{title}</b>?</div>,
                              onConfirm: async(): Promise<void> => {
                                await deleteNews(id)
                              }
                            })
                          }}
                        >
                          Elimina
                        </Button>
                      </Col>
                    </div>
                  </Card>
                )
              })}
            </div>
            :
            <Message title="Nessuna news trovata"></Message>
          }
        </>
      }
      {currentNews !== null &&
        <Modal
          close={(): void => setCurrentNews(null)}
          title="Modifica News"
        >
          <EditNewsForm
            defaultValues={currentNews}
            onSubmit={async(): Promise<void> => {
              await renderNews()
              setCurrentNews(null)
              toast.success("News aggiornata correttamente")
            }}
          />
        </Modal>
      }

      {addNewsModalOpen &&
        <Modal
          title="Aggiungi una News"
          close={(): void => setAddNewsModalOpen(false)}
        >
          <AddNewsForm
            onSuccess={async(): Promise<void> => {
              await renderNews()
              setAddNewsModalOpen(false)
              toast.success("News aggiunta correttamente")
            }}
          />
        </Modal>
      }
    </>
  )
}

export default NewsPage
